import {conditionLabel, parseCondition, TemplateCondition} from './template-conditions';

export const CONDITION_TOKEN_SELECTOR = 'span[data-qa-condition]';

/** Locates an original expression in the editor's unmodified text stream. */
export interface ConditionMarker {
  start: number;
  end: number;
  source: string;
  kind: 'if' | 'else' | 'end';
}

/** Associates all boundaries of one complete, supported conditional block. */
export interface ConditionBlock {
  id: string;
  condition: TemplateCondition;
  unless: boolean;
  opening: ConditionMarker;
  otherwise?: ConditionMarker;
  closing: ConditionMarker;
}

/** Pairs blocks with a stack, including unsupported helpers so nested closing tags cannot be misidentified. */
export function findConditionBlocks(content: string): ConditionBlock[] {
  const pattern = /\{\{(?!\{)(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^{}"'])*\}\}(?!\})/g;
  // hide comments and raw blocks while keeping source offsets stable
  const scan = content.replace(/\{\{!--[\s\S]*?--\}\}|\{\{![\s\S]*?\}\}|\{\{\{\{(\w+)[^{}]*\}\}\}\}[\s\S]*?\{\{\{\{\/\1\}\}\}\}/g,
    match => ' '.repeat(match.length));
  const stack: {name: string; block: ConditionBlock; supported: boolean}[] = [];
  const blocks: ConditionBlock[] = [];
  let match: RegExpExecArray;
  while ((match = pattern.exec(scan))) {
    if (content[match.index - 1] === '{') { continue; }
    const body = match[0].slice(2, -2).trim();
    const marker: ConditionMarker = {start: match.index, end: pattern.lastIndex, source: match[0], kind: 'if'};
    if (body[0] === '#' || /^\^\w/.test(body)) {
      const header = /^#(\w+)\s+([\s\S]+)$/.exec(body);
      const name = header?.[1] || body.slice(1).split(/\s/)[0];
      const condition = header && ['if', 'unless'].indexOf(name) !== -1 ? parseCondition(header[2]) : null;
      stack.push({name, supported: !!condition, block: {
        id: String(marker.start), condition, unless: name === 'unless', opening: marker, closing: null
      }});
    } else if (body === 'else' || body.indexOf('else ') === 0 || body === '^') {
      const current = stack[stack.length - 1];
      if (!current) { continue; }
      if (body !== 'else' || current.block.otherwise) { current.supported = false; }
      current.block.otherwise = {...marker, kind: 'else'};
    } else if (body[0] === '/') {
      const current = stack.pop();
      if (!current || current.name !== body.slice(1).trim()) {
        stack.forEach(entry => entry.supported = false);
        continue;
      }
      current.block.closing = {...marker, kind: 'end'};
      if (current.supported) { blocks.push(current.block); }
    }
  }
  return blocks.sort((left, right) => left.opening.start - right.opening.start);
}

/** Keeps source text available for copying while presenting a readable protected boundary. */
export function createConditionToken(marker: ConditionMarker, id: string, label: string): HTMLSpanElement {
  const token = document.createElement('span');
  token.className = 'qa-condition-token';
  token.setAttribute('data-qa-condition', marker.kind);
  token.setAttribute('data-qa-condition-id', id);
  token.setAttribute('data-qa-condition-label', label);
  token.setAttribute('contenteditable', 'false');
  token.setAttribute('tabindex', '0');
  token.setAttribute('role', 'button');
  token.setAttribute('aria-label', label);
  token.title = marker.source;
  token.textContent = marker.source;
  return token;
}

/** Decorates complete expressions without moving surrounding rich text or changing stored values. */
export function decorateConditionTokens(
  root: Element, labels: {[path: string]: string}, translate = (text: string) => text
): boolean {
  const blocks = findConditionBlocks(root.textContent || '');
  const markers: {marker: ConditionMarker; block: ConditionBlock; label: string}[] = [];
  blocks.forEach(block => {
    const summary = conditionLabel(block.condition, labels, translate);
    const label = `${translate(block.unless ? 'WENN NICHT' : 'WENN')} ${summary} · ${translate('DANN')}`;
    markers.push({marker: block.opening, block, label});
    if (block.otherwise) { markers.push({marker: block.otherwise, block, label: translate('SONST')}); }
    markers.push({marker: block.closing, block, label: translate('ENDE')});
  });
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: {node: Text; start: number; end: number}[] = [];
  let node: Node;
  let offset = 0;
  while ((node = walker.nextNode())) {
    nodes.push({node: node as Text, start: offset, end: offset + node.textContent.length});
    offset += node.textContent.length;
  }
  let changed = false;
  const retained = new Set<Element>();
  markers.sort((left, right) => right.marker.start - left.marker.start).forEach(({marker, block, label}) => {
    const entry = nodes.find(item => item.start <= marker.start && item.end >= marker.end);
    if (!entry || entry.node.parentElement.closest('script, style, [data-qa-variable]')) { return; }
    const existing = entry.node.parentElement.closest(CONDITION_TOKEN_SELECTOR);
    if (existing) {
      existing.setAttribute('data-qa-condition-id', block.id);
      existing.setAttribute('data-qa-condition-label', label);
      existing.setAttribute('aria-label', label);
      retained.add(existing);
      return;
    }
    const expression = entry.node.splitText(marker.start - entry.start);
    expression.splitText(marker.source.length);
    const token = createConditionToken(marker, block.id, label);
    expression.replaceWith(token);
    retained.add(token);
    changed = true;
  });
  root.querySelectorAll(CONDITION_TOKEN_SELECTOR).forEach(token => {
    if (!retained.has(token)) {
      token.replaceWith(document.createTextNode(token.textContent || ''));
      changed = true;
    }
  });
  return changed;
}

/** Serializes editor boundaries to ordinary Handlebars, including Froala's temporary selection markers. */
export function serializeConditionTokens(html: string): string {
  if (!html || html.indexOf('data-qa-condition') === -1) { return html; }
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll(CONDITION_TOKEN_SELECTOR).forEach(token => {
    const markers = Array.from(token.querySelectorAll('.fr-marker, .fr-tmp'));
    markers.forEach(marker => marker.remove());
    token.replaceWith(document.createTextNode(token.textContent || ''), ...markers);
  });
  return template.innerHTML;
}

/** Detects whether replacing a selection would leave orphaned condition boundaries. */
export function partiallySelectedCondition(root: HTMLElement, range: Range): ConditionBlock {
  if (!range || range.collapsed || !root.contains(range.commonAncestorContainer)) { return null; }
  const measure = document.createRange();
  measure.selectNodeContents(root);
  measure.setEnd(range.startContainer, range.startOffset);
  const start = measure.toString().length;
  measure.setEnd(range.endContainer, range.endOffset);
  const end = measure.toString().length;
  return findConditionBlocks(root.textContent).find(block => {
    if (start <= block.opening.start && end >= block.closing.end) { return false; }
    return [block.opening, block.otherwise, block.closing].filter(Boolean)
      .some(marker => start < marker.end && end > marker.start);
  });
}
